import { Outlet, RouteObject } from 'react-router-dom';

import {
  ProtectedRoute,
  ProtectedRouteProps,
  ProtectedUserSegmentRoute,
} from 'create/atoms/authorization/ProtectedRoute';
import { TeamResourceName } from 'create/atoms/authorization/ResourceName';
import { UserSegment } from 'types/graphql';

import { trackEditorRoutes } from './TrackEditorRoutes';

export const coreAppRoutes: RouteObject[] = [
  withNotifications(
    'create-track',
    withNavigationContainer(
      false,
      withProtectedRoute(
        { action: 'create:track', teamID: TeamResourceName.ANY },
        {
          path: '',
          lazy: () =>
            import('./pages/start-creating-content/StartCreatingContent').then(m => ({
              Component: m.StartCreatingContent,
            })),
        },
      ),
    ),
  ),
  withNotifications(
    'create-track/templates',
    withNavigationContainer(
      false,
      withProtectedRoute(
        { action: 'create:track', teamID: TeamResourceName.ANY },
        {
          path: '',
          lazy: () => import('./pages/template-library/Templates').then(m => ({ Component: m.Templates })),
        },
      ),
    ),
  ),
  withNotifications(
    ':teamSlug/tracks/:trackSlug/wizard',
    withNavigationContainer(
      false,
      withProtectedRoute(
        { action: 'create:track' },
        {
          path: '',
          lazy: () =>
            import('./pages/create-track-wizard/CreateTrackWizardPage').then(m => ({
              Component: m.CreateTrackWizardPage,
            })),
        },
      ),
    ),
  ),

  withNotifications(
    ':teamSlug/tracks/:trackSlug/editor/*',
    withNavigationContainer(false, withProtectedRoute({ action: 'update:track' }, { children: trackEditorRoutes })),
  ),

  withNotifications('logout', { lazy: () => import('./pages/logout/Logout').then(m => ({ Component: m.Logout })) }),
  withNotifications(
    'login',
    withNavigationContainer(true, {
      lazy: () => import('./pages/authentication/Authentication').then(m => ({ Component: m.LoginContainer })),
    }),
  ),
  withNotifications(
    'login-sso',
    withNavigationContainer(true, {
      lazy: () => import('./pages/authentication/Authentication').then(m => ({ Component: m.LoginSSOContainer })),
    }),
  ),
  withNotifications(
    ':teamSlug/login',
    withNavigationContainer(true, {
      lazy: () => import('./pages/team-login/TeamLogin').then(m => ({ Component: m.TeamLogin })),
    }),
  ),
  withNotifications(
    'signup',
    withNavigationContainer(true, {
      lazy: () => import('./pages/authentication/Authentication').then(m => ({ Component: m.SignupContainer })),
    }),
  ),
  withNotifications(':teamSlug/invite/:inviteID', {
    lazy: () => import('./pages/invite-entry/InviteEntry').then(m => ({ Component: m.InviteEntry })),
  }),
  withNotifications(':teamSlug/invite/:inviteID/tracks/:trackSlug', {
    lazy: () => import('./pages/track/Track').then(m => ({ Component: m.Track })),
  }),
  withNotifications(
    'users/self/edit',
    withNavigationContainer(false, {
      lazy: () =>
        import('./pages/edit-profile/EditProfileContainer').then(m => ({ Component: m.EditProfileContainer })),
    }),
  ),
  withNotifications(
    'forgot-password',
    withNavigationContainer(false, {
      lazy: () => import('./pages/authentication/Authentication').then(m => ({ Component: m.ForgotPasswordContainer })),
    }),
  ),
  withNotifications(
    ':teamSlug/inspiration-library/:templateSlug/:trackSlug',
    withNavigationContainer(false, {
      lazy: () =>
        import('./pages/inspiration-library-page-legacy/LibraryTemplate').then(({ LibraryTemplate }) => ({
          element: (
            <ProtectedUserSegmentRoute segment={UserSegment.ContentCreator}>
              <LibraryTemplate />
            </ProtectedUserSegmentRoute>
          ),
        })),
    }),
  ),
  withNotifications(
    'study-room',
    withNavigationContainer(false, {
      lazy: () => import('./pages/study-room/StudyRoom').then(m => ({ Component: m.StudyRoomContainer })),
    }),
  ),
  withNotifications(
    'not-found',
    withNavigationContainer(false, {
      lazy: () => import('./pages/not-found/NotFound').then(m => ({ Component: m.NotFound })),
    }),
  ),
  withNotifications(
    ':teamSlug/tracks/:trackSlug',
    withNavigationContainer(false, {
      lazy: () => import('./pages/track/Track').then(m => ({ Component: m.Track })),
    }),
  ),
];

function withNotifications(path: string, routeProps: RouteObject): RouteObject {
  return {
    path,
    lazy: () =>
      import('./templates/NotificationsContainer').then(({ NotificationsContainer }) => ({
        element: (
          <>
            <Outlet />
            <NotificationsContainer />
          </>
        ),
      })),

    children: [{ path: '', ...routeProps }],
  };
}

function withNavigationContainer(shouldHideAccountActions: boolean, routeProps: RouteObject): RouteObject {
  return {
    path: '',
    lazy: () =>
      import('./templates/navigation/NavigationContainer').then(({ NavigationContainer }) => ({
        element: (
          <NavigationContainer shouldHideAccountActions={shouldHideAccountActions}>
            <Outlet />
          </NavigationContainer>
        ),
      })),
    children: [{ path: '', ...routeProps }],
  };
}

function withProtectedRoute(
  protectedRouteProps: Omit<ProtectedRouteProps, 'children'>,
  routeProps: RouteObject,
): RouteObject {
  return {
    element: (
      <ProtectedRoute {...protectedRouteProps}>
        <Outlet />
      </ProtectedRoute>
    ),
    children: [routeProps],
  };
}
